/**
 * LIVING ROOM PUBLIC REPLAY PAGE
 * Anyone can view a shared Living War Room debate without logging in.
 * Shows the full debate with god avatars, messages, verdict, and a CTA to sign up.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Loader2, Copy, Check, Share2, Target, AlertTriangle, 
  Zap, Eye, Sparkles, ArrowRight, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOD_CONFIG = {
  calliope: { name: 'Calliope', emoji: '\u{1F3AD}', title: 'Creative Wildcard', gradient: 'from-pink-500 to-purple-600', text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30', glow: 'rgba(236,72,153,0.3)' },
  eris: { name: 'Eris', emoji: '\u2694\uFE0F', title: 'The Contrarian', gradient: 'from-red-600 to-pink-600', text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', glow: 'rgba(239,68,68,0.3)' },
  plutus: { name: 'Plutus', emoji: '\u{1F4B0}', title: 'The CFO', gradient: 'from-green-500 to-emerald-600', text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', glow: 'rgba(34,197,94,0.3)' },
  hestia: { name: 'Hestia', emoji: '\u{1F3E0}', title: 'Customer Advocate', gradient: 'from-orange-400 to-amber-500', text: 'text-orange-300', bg: 'bg-orange-400/10', border: 'border-orange-400/30', glow: 'rgba(251,146,60,0.3)' },
  themis: { name: 'Themis', emoji: '\u2696\uFE0F', title: 'Data Purist', gradient: 'from-blue-600 to-cyan-600', text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', glow: 'rgba(59,130,246,0.3)' },
  nike: { name: 'Nike', emoji: '\u{1F3C6}', title: 'Growth Hacker', gradient: 'from-yellow-400 to-orange-500', text: 'text-yellow-300', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', glow: 'rgba(250,204,21,0.3)' },
  metis: { name: 'Metis', emoji: '\u{1F9E0}', title: 'Grand Strategist', gradient: 'from-indigo-600 to-purple-700', text: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', glow: 'rgba(99,102,241,0.3)' },
  athena: { name: 'Athena', emoji: '\u{1F989}', title: 'Strategic Intel', gradient: 'from-amber-500 to-orange-600', text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', glow: 'rgba(245,158,11,0.3)' },
  hermes: { name: 'Hermes', emoji: '\u{1F45F}', title: 'Content God', gradient: 'from-orange-500 to-red-600', text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', glow: 'rgba(249,115,22,0.3)' },
  apollo: { name: 'Apollo', emoji: '\u2600\uFE0F', title: 'Distribution', gradient: 'from-yellow-500 to-orange-500', text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', glow: 'rgba(234,179,8,0.3)' },
  dionysus: { name: 'Dionysus', emoji: '\u{1F377}', title: 'Viral Growth', gradient: 'from-purple-600 to-red-600', text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', glow: 'rgba(147,51,234,0.3)' },
  ares: { name: 'Ares', emoji: '\u{1F5E1}', title: 'God of War', gradient: 'from-red-600 to-rose-700', text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', glow: 'rgba(239,68,68,0.3)' },
  personal_god: { name: 'The Thirteenth', emoji: '\u{1F441}', title: 'Your Brand God', gradient: 'from-amber-400 via-yellow-300 to-amber-500', text: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-400/30', glow: 'rgba(251,191,36,0.5)', isPersonalGod: true },
};

const LivingRoomReplayPage = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/war-council/living-room/shared/${shareId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || 'Debate not found');
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shareId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading divine debate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-white mb-2">Debate Not Found</h2>
          <p className="text-gray-400 text-sm mb-6">{error}</p>
          <Link to="/" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium hover:from-purple-500 hover:to-cyan-500 transition-all">
            Visit iSupplyAI
          </Link>
        </div>
      </div>
    );
  }

  const gods = data?.gods || [];
  const messages = data?.messages || [];
  const topic = data?.topic || '';
  const views = data?.views || 0;

  return (
    <div className="min-h-screen bg-[#030305]" data-testid="living-room-replay">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[180px]" />
        <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] bg-cyan-600/5 rounded-full blur-[180px]" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="font-semibold">iSupplyAI</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-600 flex items-center gap-1">
              <Eye className="w-3 h-3" /> {views} views
            </span>
            <button onClick={copyLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-gray-400 hover:text-white hover:border-purple-500/30 transition-colors text-xs" data-testid="copy-link">
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>

        {/* Title Card */}
        <div className="rounded-xl border border-purple-500/20 bg-gradient-to-b from-purple-900/20 to-gray-900/50 overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-purple-500/10">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] text-gray-500 uppercase tracking-widest font-medium">The Living War Room</span>
            </div>
            <p className="text-purple-200/80 italic text-sm leading-relaxed" data-testid="replay-topic">"{topic}"</p>
          </div>
          <div className="px-5 py-3 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider mr-1">Today's council:</span>
            {gods.map((id) => {
              const god = GOD_CONFIG[id];
              if (!god) return null;
              return (
                <span key={id} className={`text-[11px] px-1.5 py-0.5 rounded-full ${god.bg} ${god.border} border ${god.text}`}>
                  {god.emoji} {god.name}
                </span>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-4 mb-6">
          {messages.map((msg, i) => {
            const isPersonalGod = msg.agent === 'personal_god' || msg.is_personal_god;
            const god = isPersonalGod
              ? { ...GOD_CONFIG.personal_god, name: msg.personal_god_name || 'The Thirteenth' }
              : (GOD_CONFIG[msg.agent] || GOD_CONFIG.calliope);
            return (
              <div key={i} data-testid={`replay-msg-${i}`}>
                {isPersonalGod && (
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/20">
                      <Crown className="w-2.5 h-2.5 text-amber-400" />
                      <span className="text-[9px] text-amber-400 uppercase tracking-widest font-semibold">The Thirteenth Speaks</span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${god.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}
                       style={{ boxShadow: `0 0 ${isPersonalGod ? '20' : '10'}px ${god.glow}` }}>
                    {isPersonalGod ? <Eye className="w-4 h-4 text-amber-900" /> : <span className="text-sm">{god.emoji}</span>}
                  </div>
                  <div className={`flex-1 px-4 py-3 rounded-2xl rounded-tl-sm ${isPersonalGod ? 'bg-amber-500/[0.06]' : 'bg-gray-800/40'} border ${god.border}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-xs font-semibold ${god.text}`}>{god.name}</span>
                      <span className="text-[10px] text-gray-600">{god.title}</span>
                      {isPersonalGod && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/20 text-amber-400 font-semibold">YOUR GOD</span>
                      )}
                    </div>
                    <p className={`${isPersonalGod ? 'text-amber-100/90' : 'text-gray-200'} text-sm leading-relaxed whitespace-pre-wrap`}>{msg.content}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Verdict */}
        {data?.verdict && (
          <div className="rounded-xl border border-purple-500/20 bg-purple-900/10 p-5 mb-6" data-testid="replay-verdict">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-purple-400 uppercase tracking-wider font-semibold">Council Verdict</span>
            </div>
            <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">{typeof data.verdict === 'string' ? data.verdict : data.verdict?.summary || data.verdict?.content || JSON.stringify(data.verdict)}</p>
          </div>
        )}

        {/* CTA Banner */}
        <div className="rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-900/30 via-gray-900/50 to-cyan-900/30 p-6 text-center" data-testid="replay-cta">
          <h3 className="text-lg font-bold text-white mb-2">Want the gods to debate YOUR strategy?</h3>
          <p className="text-gray-400 text-sm mb-4 max-w-md mx-auto">
            12 AI gods with individual brains, personalized to your business. They remember past debates. They disagree. They evolve.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-medium text-sm hover:from-purple-500 hover:to-cyan-500 transition-all" data-testid="replay-signup-btn">
            Enter the Pantheon <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-[10px] text-gray-600 mt-3">Free to start. No credit card required.</p>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-[10px] text-gray-700">
          Powered by iSupplyAI — 12 AI Gods. One Strategy.
        </div>
      </div>
    </div>
  );
};

export default LivingRoomReplayPage;
